import { ApolloError, gql, useQuery } from '@apollo/client';
import { useSelector, useDispatch } from 'react-redux';
// import * as Sentry from '@sentry/react';

import { ALL_QUESTION_FIELDS } from '../fragments/question.fragments';

import {
  getFirstValue,
  squashEdges,
  transformInserts,
  useProcessLabel,
  useTransformEdges,
} from '../util/kb';

import { ViewableQuestion } from '../types/global';
import { Question, QuestionsData, Response } from '../types/kb';
import { checkObjectConditions } from '../util/graph';
import { useCheckCondition } from './useCheckCondition';
import { useSpecialty, useVersion } from './useVersion';
import { RootState } from '../types/graph';
import { useEffect, useMemo } from 'react';
import { initialQuestionIds } from '@aldebaran/common/constants/kb';
import {
  UpdateQuestionsPayload,
  updateSavedRawQuestions,
} from '@/reducers/graph';

const GET_QUESTIONS_QUERY = gql`
  ${ALL_QUESTION_FIELDS}
  query Question($version: Float!, $specialty: String, $extIds: [String]!) {
    questions(
      where: {
        version: { eq: $version }
        specialty: { eq: $specialty }
        extId_IN: $extIds
      }
    ) {
      ...AllQuestionFields
    }
  }
`;

export const useQuestions = (
  questionIds: Question['extId'][],
  onCompleted?: (payload: UpdateQuestionsPayload) => void,
  onError?: (error: ApolloError) => void,
) => {
  const dispatch = useDispatch();
  const savedRawQuestions = useSelector(
    (state: RootState) => state.kb.savedRawQuestions,
  );

  const savedRawQuestionIds = useMemo(
    () => savedRawQuestions.map((q) => q.extId),
    [savedRawQuestions],
  );

  const filteredQuestionIds = useMemo(
    () => questionIds.filter((id) => !savedRawQuestionIds.includes(id)),
    [questionIds, savedRawQuestionIds],
  );

  const version = useVersion();
  const specialty = useSpecialty();

  const transformEdges = useTransformEdges();
  const processLabel = useProcessLabel();
  const checkCondition = useCheckCondition();

  const currentPage = useSelector(
    (state: RootState) => state.graph.currentPage,
  );
  const insertedBy = useSelector(
    (state: RootState) => state.questionnaire.insertedBy,
  );
  const pageAssignments = useSelector(
    (state: RootState) => state.questionnaire.pageAssignments,
  );
  const sectionAssignments = useSelector(
    (state: RootState) => state.questionnaire.sectionAssignments,
  );
  const userResponses = useSelector((state: RootState) => state.userResponses);

  const questionnaireConfig = useSelector(
    (state: RootState) => state.questionnaire.config,
  );

  const { data, loading, error, refetch } = useQuery<QuestionsData>(
    GET_QUESTIONS_QUERY,
    {
      variables: { version, specialty, extIds: filteredQuestionIds },
      skip: !questionnaireConfig,
      errorPolicy: 'all',
    },
  );

  // Refetch when internet comes back after a network error
  useEffect(() => {
    const refetchKB = () => {
      if (error && !loading && filteredQuestionIds.length > 0) {
        refetch();
      }
    };

    window.addEventListener('online', refetchKB);
    return () => window.removeEventListener('online', refetchKB);
  }, [error, loading, filteredQuestionIds.length, refetch]);

  useEffect(() => {
    if (data?.questions) {
      dispatch(updateSavedRawQuestions(data.questions));
    }
  }, [data?.questions, dispatch]);

  // ALDO-2698: keep question IDs referentially stable when their contents are unchanged.
  const questionIdsKey = questionIds.join('\u0000');
  const stableQuestionIds = useMemo(
    () => (questionIdsKey ? questionIdsKey.split('\u0000') : []),
    [questionIdsKey],
  );

  const mergedDataQuestions = useMemo(
    () =>
      [...savedRawQuestions, ...(data?.questions ?? [])].filter((q) =>
        stableQuestionIds.includes(q.extId),
      ),
    [savedRawQuestions, data?.questions, stableQuestionIds],
  );

  // To make sure we have no question inserted before the current question
  // const transformAfter = (afterExtId: string, insertedByExtId: string) => {
  //   const indexOfAfter = questionIds.indexOf(afterExtId);
  //   const indexOfCurrentQuestion = questionIds.indexOf(insertedByExtId);
  //   if (
  //     insertedByExtId !== '22' &&
  //     insertedByExtId !== '40' &&
  //     indexOfAfter < indexOfCurrentQuestion
  //   ) {
  //     Sentry.captureMessage(
  //       `La question ${afterExtId} apparaît AVANT la question qui l'insère (${insertedByExtId})`,
  //       {
  //         level: 'error',
  //         tags: {
  //           question_ordering: 'invalid_after_reference',
  //           insertedByExtId,
  //           afterExtId,
  //         },
  //         extra: {
  //           questionIds,
  //           indexOfAfter,
  //           indexOfCurrentQuestion,
  //           timestamp: new Date().toISOString(),
  //         },
  //       },
  //     );
  //   }
  //   return indexOfAfter < indexOfCurrentQuestion ? insertedByExtId : afterExtId;
  // };

  useEffect(() => {
    // Don't dispatch while Apollo is mid-fetch — the data may be stale.
    // When the fetch completes, this effect will re-run with fresh data.
    if (loading) {
      return;
    }

    if (error) {
      console.error(error);
      onError?.(error);
    }
    if (!onCompleted || !data?.questions || !mergedDataQuestions) {
      return;
    }
    const questions: ViewableQuestion[] = mergedDataQuestions
      // Object-level gate (ALDO-2587): does this question exist in this run
      // at all? Decided on the raw object — before paying for label, section
      // and response resolution — with the question's own assertions
      // excluded (same self-exclusion as label conditions). Applies to
      // onboarding questions too: they are only exempt from the label rule
      // below. Several condition lines OR together: one holding line is
      // enough (ALDO-2641). Each line is checked on its own, so a !!
      // alternation inside one line never bleeds into the others.
      .filter((question) =>
        checkObjectConditions(question.conditions, (condition) =>
          checkCondition(condition, question.extId),
        ),
      )
      .map((question) => ({
        ...question,
        extId: question.extId,
        section:
          typeof question.section === 'string'
            ? question.section
            : getFirstValue(transformEdges(question.sectionsConnection)) ||
              sectionAssignments[question.extId],
        label: processLabel(
          getFirstValue(
            transformEdges(question.labelsConnection, question.extId),
          ) ?? null,
        ),
        comment: processLabel(
          getFirstValue(transformEdges(question.commentsConnection)),
        ),
        placeholder: processLabel(
          getFirstValue(transformEdges(question.placeholdersConnection)),
        ),
        after:
          transformEdges(question.afterConnection)?.[0]?.extId ??
          insertedBy[question.extId] ??
          null,
        hideResponsesByDefault: question.hideResponsesByDefault,
        responses: question.responses
          // Object-level gate, before the mapping work: evaluated with the
          // parent question's assertions excluded so a response cannot hide
          // itself or its siblings by being selected. The lines OR together,
          // same as on the question.
          .filter((r) =>
            checkObjectConditions(r.conditions, (condition) =>
              checkCondition(condition, question.extId),
            ),
          )
          .map((r: Response) => ({
            ...r,
            question: transformEdges(r.questionConnection)?.[0]?.extId,
            format: r.format ?? question.format,
            insertsQuestions: transformInserts(r.insertsQuestions),
            deletesQuestions: transformInserts(r.deletesQuestions),
            reports: (() => {
              const reportEdges = squashEdges(r.reportsConnection);
              if (reportEdges.length === 0) return null;
              return reportEdges.map((report) => {
                const { explanationsConnection, ...rest } = report;
                const explanationEdges = explanationsConnection
                  ? squashEdges(explanationsConnection)
                  : [];
                return {
                  ...rest,
                  condition: rest.condition ?? '',
                  explanations:
                    explanationEdges.length === 0
                      ? null
                      : explanationEdges.map((e) => ({
                          ...e,
                          condition: e.condition ?? '',
                        })),
                };
              });
            })(),
            label: processLabel(
              getFirstValue(
                transformEdges(r.labelsConnection, question.extId),
              ) ?? null,
            ),
            unit: transformEdges(r.unitConnection)?.[0],
            minimum: getFirstValue(transformEdges(r.minimumConnection)),
            maximum: getFirstValue(transformEdges(r.maximumConnection)),
            increment: getFirstValue(transformEdges(r.incrementConnection)),
            checklistItems: transformEdges(r.checklistItemsConnection),
            comment: processLabel(
              getFirstValue(transformEdges(r.commentsConnection)),
            ),
          }))
          .filter((r) => r.label !== null)
          .sort((a, b) => a.extId.localeCompare(b.extId)),
      }))
      .filter((q) => q.label !== null || initialQuestionIds[q.extId]);
    onCompleted({
      questions,
      coveredIds: mergedDataQuestions.map((q) => q.extId),
    });
  }, [
    loading,
    data,
    error,
    onCompleted,
    onError,
    insertedBy,
    currentPage,
    pageAssignments,
    sectionAssignments,
    userResponses,
    mergedDataQuestions,
  ]);

  return { loading };
};
